/**
 * One signage screen's live overview: is it playing, what is on right now, and
 * what the next twelve hours look like.
 *
 * ⚠️ This lived on `SignagePlanPage`, a page that was NEVER ROUTED - written
 * once and never imported, so everything on it was unreachable. Extracted here
 * so it can be used from the scheduling page, where an operator has already
 * picked the screen they are asking about.
 *
 * The rail answers from the SAME resolver the panel is answered with, so what
 * an operator reads here is what the screen does rather than a second
 * implementation of the rules - with two deliberate exceptions that read the
 * panel itself rather than the schedule: the live badge, and (since
 * 2026-09-21) the elapsed/progress readout, which prefers the panel's own
 * `itemStartedAt` over the schedule's theoretical position so it can
 * actually answer "is this stuck" - see `liveElapsedSeconds`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Divider,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SignageTimeline, { stripGeometry } from "./SignageTimeline";
import SignageScreenSettingsDialog from "./SignageScreenSettingsDialog";
import SignageLiveBadge from "./SignageLiveBadge";
import { signageItemLabel } from "../utils/signageItems";
import { foyerApi } from "../http/foyer";
import {
  rotationMismatch,
  unitOrientation,
  type ScreenOrientation,
} from "@shared/media";
import { errorText } from "../utils/apiError";
import type { SignagePlaybackReport } from "@shared/types/foyer";
import { tokens } from "../theme";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import {
  breakAt,
  channelPosterUrl,
  formatClock,
  formatDuration,
  localDayInZone,
  nextBreakInSeconds,
  playheadAt,
  zonedTimeToUtc,
} from "@shared/signage";
import SignageCampaignLanes from "./SignageCampaignLanes";
import SignageChangeChannelDialog from "./SignageChangeChannelDialog";
import { useLinkedScroll } from "../utils/linkedScroll";
import { useNavigate } from "react-router-dom";
import type {
  SignageAssignment,
  SignageCampaign,
  SignagePlaylist,
  SignageTimeline as Timeline,
} from "@shared/types/signage";
import { usePermission } from "../auth/access";

/**
 * Signage content plan - the estate view.
 *
 * One card per screen: what it is set to play, and a scrolling strip of what it
 * will actually play over the next few hours with a live playhead. The strip is
 * rendered from the SAME resolver that answers a player, so what an operator
 * sees here is what the screen does - not a second implementation of the rules.
 *
 * BOTH PLAYER GENERATIONS ARE SERVED FROM THIS ONE PLAN. Everything surfaced on
 * a card - default content, orientation, sound, campaign break cadence - maps
 * onto the legacy `/api/signage` payload as well as onto a Foyer client, so a
 * site can run a mix of new Foyer screens and fielded autoplayers off the same
 * schedule. Nothing here may grow a control that only one generation can honour
 * without saying so on the card itself.
 */

/** How far ahead each strip resolves. */
const HORIZON_HOURS = 24;

const META_SEPARATOR = "  ·  ";

/**
 * How far through the item on screen we are, as a percentage.
 *
 * Zero-length is 0 rather than a division by zero, and that is a real case
 * rather than a defensive habit: a video set to "play to its natural end"
 * carries `durationSeconds: 0`, and a live channel carries it deliberately.
 * A full bar for something with no known length would be a confident lie
 * about the one thing this bar is asked.
 */
export function progressPct(elapsed: number, total: number): number {
  if (!(total > 0)) return 0;
  return Math.max(0, Math.min(100, (elapsed / total) * 100));
}

/**
 * How long the panel ITSELF says it has been on the current item, in
 * seconds - not the schedule's theoretical position.
 *
 * 2026-09-21 (hasselbacken/302): the elapsed readout below used to be pure
 * `playheadAt`/`breakAt` math - what the loop SHOULD be doing - which reads
 * identically whether the panel is genuinely mid-clip or has been frozen on
 * one for twenty minutes. `itemStartedAt` (`SignagePlaybackReport`, added
 * the same day) is real telemetry from the device; preferring it here is
 * what makes this readout answer "is it actually moving" instead of always
 * reading like a healthy screen. Returns `null` - not 0 - when the panel
 * has never reported it (an older build) or is not currently `playing`, so
 * the caller can fall back to the schedule's own number, which is still the
 * best available answer for a screen genuinely dark since Tuesday.
 */
function liveElapsedSeconds(
  report: SignagePlaybackReport | undefined,
  now: Date
): number | null {
  if (!report || report.state !== "playing" || !report.itemStartedAt) {
    return null;
  }
  const startedAt = Date.parse(report.itemStartedAt);
  if (!Number.isFinite(startedAt)) return null;
  return Math.max(0, (now.getTime() - startedAt) / 1000);
}

export interface SignageScreenOverviewProps {
  /*
   * The minimal shape rather than a `ScreenRecord`, because the two callers
   * hold different projections of a screen and neither should have to fetch
   * the other's. Liveness is passed IN for the same reason it is shown at all:
   * it is the one fact here that does not come from the timeline.
   */
  screenId: string;
  screenLabel: string;
  /** Last heartbeat, for the live badge. */
  lastSeenAt: string | null | undefined;
  /** The panel's own account of what it is playing. */
  report: SignagePlaybackReport | undefined;
  premiseId: string;
  /**
   * The premise's own zone. The strip is a LOCAL day, and which instant that
   * starts at is unknowable without it.
   */
  timezone: string;
  assignment: SignageAssignment | undefined;
  playlists: SignagePlaylist[];
  now: Date;
  /** URL -> the name somebody gave the file. Absent until the library lands. */
  assetName: (url: string) => string | undefined;
  /** URL -> a picture of it: an image, or a video's poster frame. */
  assetThumb: (url: string) => string | undefined;
  /** Campaigns booked onto THIS screen, drawn as lanes under the content. */
  campaigns: SignageCampaign[];
  /**
   * Unpair this screen. Absent = the option is not offered at all.
   *
   * Passed in rather than called here so the page can refresh its list, and
   * so a surface that should not offer removal simply does not pass it.
   */
  onUnpair?: () => Promise<void>;
  /**
   * The screen's unit (UNIT-2): its id, how it is hung and how many panels
   * it drives. Absent = the screen is in no unit.
   */
  unit?: {
    id: string;
    orientation?: ScreenOrientation;
    panelCount?: number;
    /** The resolution its screen last reported. */
    reportedResolution?: string;
  };
  /** Flip a campaign's priority from its lane label. */
  onSetPriority?: (
    campaign: SignageCampaign,
    priority: "normal" | "high"
  ) => Promise<void>;
}

export default function SignageScreenOverview({
  screenId,
  screenLabel,
  lastSeenAt,
  report,
  premiseId,
  timezone,
  assignment,
  playlists,
  now,
  assetName,
  assetThumb,
  campaigns,
  unit,
  onSetPriority,
  onUnpair,
}: SignageScreenOverviewProps) {
  /**
   * The content strip and the campaign lanes move as one.
   *
   * They are separate scroll containers because each carries its own pinned
   * rail, but they are one picture of one day - and the whole reason for
   * stacking them is that "which campaign is eligible while THAT playlist is
   * on" is a vertical line. Scrolled apart, that line lies.
   */
  const toast = useToast();
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const linked = useLinkedScroll();
  const navigate = useNavigate();
  /** Campaign lanes are shown by default and can be folded away per card. */
  const [lanesOpen, setLanesOpen] = useState(true);
  const [channelOpen, setChannelOpen] = useState(false);

  const unpair = async () => {
    // Named, and spelling out the consequence: "are you sure" tells somebody
    // nothing they did not already know.
    if (
      !window.confirm(
        t("signageScreenOverview.removeConfirm", { name: screenLabel })
      )
    ) {
      return;
    }
    await onUnpair?.();
  };
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const canEditSignage = usePermission("signage", "edit");
  // The card's own copy, so a save shows immediately rather than after a
  // reload. The page re-fetches assignments on its own schedule.
  const [local, setLocal] = useState<SignageAssignment | undefined>(assignment);
  useEffect(() => setLocal(assignment), [assignment]);
  // The unit's orientation, kept locally so a change shows at once.
  const [unitShape, setUnitShape] = useState<ScreenOrientation | undefined>(
    unit?.orientation
  );
  useEffect(() => setUnitShape(unit?.orientation), [unit?.orientation]);
  const reportedResolution = unit?.reportedResolution;
  const shape = unitOrientation(unitShape, reportedResolution);
  const mismatch = rotationMismatch(unitShape, reportedResolution);

  /**
   * Midnight in the PREMISE's zone, as a real UTC instant.
   *
   * Recomputed only when the local date changes, so the strip does not
   * re-fetch every second - and so a screen watched across midnight rolls onto
   * the new day on its own.
   */
  const dayStart = useMemo(
    () => zonedTimeToUtc(timezone, localDayInZone(timezone, now).ymd, 0),
    // The DATE, not the instant: `now` ticks every second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timezone, localDayInZone(timezone, now).ymd]
  );

  useEffect(() => {
    let alive = true;
    foyerApi
      .getSignageTimeline(premiseId, screenId, {
        // The DAY, 00:00 to 23:59, not a rolling window from now. An operator
        // reads a strip against the clock on the wall, and one that starts at
        // 14:00 makes them do arithmetic to find this morning.
        at: dayStart,
        hours: HORIZON_HOURS,
      })
      .then((resolved) => alive && setTimeline(resolved))
      .catch((e: unknown) => alive && setError(errorText(e, t)));
    return () => {
      alive = false;
    };
    // Re-resolve when the assignment changes - a new default or day plan
    // changes the strip, and the revision hash alone would not tell us.
    // `dayStart` participates: crossing midnight is a new strip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId, screenId, assignment?.updatedAt, dayStart]);

  /**
   * The TIMELINE's own playlists first, the page's list second.
   *
   * ⚠️ This read the page's premise-scoped list alone and printed "Unknown
   * playlist" over blocks that were perfectly well known - a live channel
   * block (whose playlist is synthesised at resolve time and exists in no
   * stored list), and anything inherited from the company or global layer.
   * The resolver already materialises exactly the playlists a timeline
   * references, which makes it the authority; the page's list only adds names
   * for something the strip is not drawing anyway.
   */
  const byId = useMemo(() => {
    const map = new Map(playlists.map((p) => [p.id, p]));
    for (const p of timeline?.playlists ?? []) map.set(p.id, p);
    return map;
  }, [playlists, timeline]);
  const nameOf = useCallback(
    (id: string) =>
      byId.get(id)?.name ?? t("signageScreenOverview.unknownPlaylist"),
    [byId, t]
  );
  /*
   * The channel's POSTER, not its first stream url.
   *
   * This drew `items[0].assetUrl` straight into an `<img>`, and once the
   * timeline materialises a catalogue channel that url is a DASH manifest -
   * so every effect channel showed a broken-image icon on the strip, in both
   * Chrome and Safari. `channelPosterUrl` states the rule once and refuses to
   * hand back anything that is not a picture.
   */
  const thumbOf = useCallback(
    (id: string) => {
      const playlist = byId.get(id);
      if (!playlist) return null;
      // The channel's own poster first - a Nenda channel has one and it is the
      // picture somebody chose for it.
      const own = channelPosterUrl(playlist);
      if (own) return own;
      /*
       * Then the media library's frame for the first clip.
       *
       * A customer's own loop has no channel poster and its first item is an
       * mp4, which no `<img>` will draw - so this is the only way it gets a
       * face. `thumbnailFor` knows a video's poster from an image's own url.
       */
      const first = playlist.items[0]?.assetUrl;
      return (first ? assetThumb(first) : undefined) ?? null;
    },
    [byId, assetThumb]
  );

  /**
   * What the screen falls back to when no plan covers the moment.
   *
   * ⚠️ THREE answers, not two, and the third is a fault. A default pointing at
   * a playlist that no longer exists is not "unknown" - it is a screen that
   * will show nothing outside its planned hours, and the operator who deleted
   * that playlist has no other way to find out. Dev data has exactly this
   * today: a `defaultPlaylistId` whose playlist is gone.
   */
  const defaultMissing = Boolean(
    local?.defaultPlaylistId && !byId.has(local.defaultPlaylistId)
  );
  const defaultName = !local?.defaultPlaylistId
    ? t("signageScreenOverview.defaultNone")
    : defaultMissing
      ? t("signageScreenOverview.deletedPlaylist")
      : nameOf(local.defaultPlaylistId);
  const orientationLabel = (o: ScreenOrientation) =>
    o === "portrait"
      ? t("common:labels.portrait")
      : t("common:labels.landscape");

  // Every fact on this line is honoured by both player generations.
  const meta = [
    // Named as a fault rather than shrugged at - see `defaultMissing`.
    defaultMissing
      ? t("signageScreenOverview.meta.defaultContentMissing", {
          name: defaultName,
        })
      : t("signageScreenOverview.meta.defaultContent", { name: defaultName }),
    // The UNIT's shape (UNIT-2), and a warning when the screen disagrees.
    mismatch
      ? t("signageScreenOverview.meta.orientationMismatch", {
          orientation: orientationLabel(shape),
          reported: t(`signageScreenOverview.orientationInline.${mismatch}`),
        })
      : orientationLabel(shape),
    local?.soundEnabled
      ? t("signageScreenOverview.meta.soundOn")
      : t("signageScreenOverview.meta.soundOff"),
    t("signageScreenOverview.meta.campaignBreaks", {
      count: local?.breaksPerHour ?? 0,
    }),
    // Absent rather than "1 panel": nobody has said, and that is the reason an
    // impression multiplier cannot be computed for this screen.
    typeof unit?.panelCount === "number"
      ? t("signageScreenOverview.meta.panels", { count: unit.panelCount })
      : t("signageScreenOverview.meta.panelsNotSet"),
  ].join(META_SEPARATOR);

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.divider}`,
        borderRadius: "12px",
        bgcolor: tokens.surface,
        p: 2.5,
        boxShadow: tokens.shadowCard,
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            flexShrink: 0,
            borderRadius: "8px",
            bgcolor: tokens.brandTintSoft,
            display: "grid",
            placeItems: "center",
            color: tokens.brand,
          }}
        >
          <TvOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              typography: "title",
              fontWeight: "fontWeightSemibold",
              letterSpacing: "0.36px",
              color: tokens.ink,
            }}
          >
            {screenLabel}
          </Typography>
          <Typography
            variant="bodySm"
            sx={{ color: tokens.inkMuted, mt: 0.25 }}
          >
            {meta}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => setMenuEl(e.currentTarget)}
          aria-label={t("signageScreenOverview.actionsFor", {
            name: screenLabel,
          })}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={() => setMenuEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {/*
            The dialog asks HOW first - now, or scheduled - because the two
            answers write to completely different places: an override that
            outranks the week, or the week itself. See the dialog.
          */}
          <MenuItem
            onClick={() => {
              setMenuEl(null);
              setChannelOpen(true);
            }}
          >
            <SwapHorizIcon fontSize="small" style={{ marginRight: 8 }} />
            {t("signageScreenOverview.changeChannel")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuEl(null);
              setLanesOpen((open) => !open);
            }}
            disabled={campaigns.length === 0}
          >
            <CampaignOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
            {/* "Hide campaigns (0)" is nonsense on a disabled row - there is
                nothing to hide. The empty case says what is true instead. */}
            {campaigns.length === 0
              ? t("signageScreenOverview.noCampaigns")
              : lanesOpen
                ? t("signageScreenOverview.hideCampaigns", {
                    count: campaigns.length,
                  })
                : t("signageScreenOverview.showCampaigns", {
                    count: campaigns.length,
                  })}
          </MenuItem>
          {canEditSignage && (
            <MenuItem
              onClick={() => {
                setMenuEl(null);
                setSettingsOpen(true);
              }}
            >
              <SettingsOutlinedIcon
                fontSize="small"
                style={{ marginRight: 8 }}
              />
              {t("signageScreenOverview.screenSettings")}
            </MenuItem>
          )}
          {onUnpair && (
            <>
              <Divider />
              {/*
                Destructive and irreversible from here: the TV stops playing
                and somebody has to stand in front of it to pair it again. It
                is last, separated, red, and confirmed by name - a misclick on
                an overview page should not be able to darken a wall.
              */}
              <MenuItem
                onClick={() => {
                  setMenuEl(null);
                  void unpair();
                }}
                sx={{ color: tokens.error }}
              >
                <DeleteOutlineIcon
                  fontSize="small"
                  style={{ marginRight: 8 }}
                />
                {t("signageScreenOverview.removeScreen")}
              </MenuItem>
            </>
          )}
        </Menu>
      </Stack>

      {error && (
        <Typography variant="bodySm" sx={{ color: tokens.error }}>
          {t("signageScreenOverview.resolveFailed", { reason: error })}
        </Typography>
      )}

      {!timeline && !error && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={20} />
        </Stack>
      )}

      {timeline && (
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: tokens.surface,
          }}
        >
          <SignageTimeline
            timeline={timeline}
            now={now}
            playlistName={nameOf}
            playlistThumb={thumbOf}
            override={local?.override ?? null}
            flush
            scrollSync={linked.register}
            rail={
              <ScreenRail
                timeline={timeline}
                lastSeenAt={lastSeenAt}
                report={report}
                assetName={assetName}
                campaigns={campaigns}
              />
            }
          />
          {/*
            The lanes, in the SAME card and on the SAME ruler. As a second card
            below they read as an unrelated picture of a different thing; here
            "which campaign is eligible while THAT playlist is on" is a
            vertical line.
          */}
          <SignageCampaignLanes
            campaigns={lanesOpen ? campaigns : []}
            geom={stripGeometry(timeline.generatedAt, timeline.horizonEndsAt)}
            now={now}
            timezone={timezone}
            onSetPriority={onSetPriority}
            scrollSync={linked.register}
          />
        </Box>
      )}

      <SignageChangeChannelDialog
        open={channelOpen}
        targetName={screenLabel}
        targetScreens={1}
        playlists={playlists}
        onClose={() => setChannelOpen(false)}
        onPlayNow={async (id, until) => {
          // The dialog owns the clock now: it asks "when should this stop" and
          // hands back the answer. This used to convert minutes into an
          // instant, which meant two places doing arithmetic on the same clock
          // and neither of them owning it.
          const playlistName =
            playlists.find((p) => p.id === id)?.name ??
            t("glossary:playlist_definite_one");
          try {
            const saved = await foyerApi.putSignageAssignment(
              premiseId,
              screenId,
              {
                override: {
                  playlistId: id,
                  since: new Date().toISOString(),
                  until,
                },
              }
            );
            setLocal(saved);
            toast.success(
              t("signageScreenOverview.nowPlays", {
                screen: screenLabel,
                playlist: playlistName,
              })
            );
          } catch (e) {
            toast.error(
              describeError(t, "changeScreenChannel", e, {
                screen: screenLabel,
              })
            );
            throw e;
          }
        }}
        onSchedule={() => navigate("/signage/plan")}
      />

      <SignageScreenSettingsDialog
        open={settingsOpen}
        screenLabel={screenLabel}
        assignment={local}
        unit={
          unit
            ? {
                id: unit.id,
                orientation: unitShape,
                reportedResolution,
              }
            : undefined
        }
        onSaveOrientation={async (orientation) => {
          if (!unit) return;
          try {
            await foyerApi.setUnitOrientation(premiseId, unit.id, orientation);
            setUnitShape(orientation ?? undefined);
          } catch (e) {
            toast.error(
              describeError(t, "changeUnitOrientation", e, {
                room: unit.id,
              })
            );
            throw e;
          }
        }}
        onClose={() => setSettingsOpen(false)}
        onSave={async (patch) => {
          try {
            const saved = await foyerApi.putSignageAssignment(
              premiseId,
              screenId,
              patch
            );
            setLocal(saved);
            toast.success(
              t("signageScreenOverview.settingsSaved", { screen: screenLabel })
            );
          } catch (e) {
            toast.error(
              describeError(t, "saveScreenSettings", e, {
                screen: screenLabel,
              })
            );
            throw e;
          }
        }}
      />
    </Box>
  );
}

/**
 * The card's left column - what is on RIGHT NOW.
 *
 * Its own component with its own clock, and that is the whole point.
 *
 * The elapsed readout ("00:21 / 00:30") and its bar are the only things on
 * this page that genuinely need a second-by-second answer. They used to get it
 * from a clock owned by the PAGE, so every tick re-rendered every screen card
 * in full - six strips, six rulers, six sets of campaign lanes, sixty times a
 * minute - to move one number.
 *
 * The clock now lives with the thing that needs it. Everything else on the
 * card follows the page's slower tick, which is all a playhead moving a
 * quarter of a pixel per second ever needed.
 */
function ScreenRail({
  timeline,
  lastSeenAt,
  report,
  assetName,
  campaigns,
}: {
  timeline: Timeline | null;
  lastSeenAt?: string | null;
  report: SignagePlaybackReport | undefined;
  assetName: (assetUrl: string) => string | undefined;
  campaigns: SignageCampaign[];
}) {
  const { t } = useTranslation(["signage", "glossary"]);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // The real number, when the panel has one - see `liveElapsedSeconds`'s own
  // doc comment for why this reads honestly stuck instead of always healthy.
  const liveElapsed = liveElapsedSeconds(report, now);

  const head = timeline ? playheadAt(timeline, now) : null;
  /*
   * A BREAK OWNS THE WALL, so the card must say so.
   *
   * Everything in this rail used to come from `playheadAt` alone, which
   * answers what the LOOP is doing. Once breaks actually play, that made the
   * card lie for a minute at a time: "Now playing: sunset bar" while a
   * campaign promo was on the wall. The same resolver the panel uses is asked
   * here, for the same reason the strip is drawn from the resolver rather than
   * from a second implementation of the rules.
   */
  const brk = timeline ? breakAt(timeline, now) : null;
  /*
   * Read off the resolved strip, not off the assignment: an override can be
   * stored and not be running - expired, not yet open, or naming a playlist
   * somebody deleted.
   */
  const overrideActive = (timeline?.content ?? []).some(
    (c) =>
      c.source === "override" &&
      Date.parse(c.startsAt) <= now.getTime() &&
      now.getTime() < Date.parse(c.endsAt)
  );

  /*
   * "Next break in Xm" - the question this card had no answer to.
   *
   * A campaign break fires for `breakSeconds` out of every `3600 /
   * breaksPerHour`, commonly a strip of only a few percent of the hour. An
   * operator who glances at "Now playing" between markers sees the ordinary
   * loop and has no way to tell "this campaign is not running" from "I am
   * looking at the 93% of the clock where it correctly is not". Only shown
   * while nothing IS currently on break - once one owns the wall, the
   * progress bar above already answers "how much longer".
   */
  const nextBreakSeconds =
    timeline && !brk ? nextBreakInSeconds(timeline, now) : null;

  return head ? (
    <Stack gap={0.25}>
      {/*
                  The ONLY thing on this card read from the panel rather than
                  from the schedule. Everything below is what the screen is
                  MEANT to play, which is equally true of a wall that has been
                  dark since Tuesday.
                */}
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
        <SignageLiveBadge lastSeenAt={lastSeenAt} report={report} now={now} />
        {/*
                      Beside "live", not instead of it. They answer two
                      different questions - "is this wall talking to us" and
                      "is it playing what the week says" - and a takeover on a
                      screen that has been dark since Tuesday is exactly the
                      case where collapsing them would mislead.
                    */}
        {overrideActive && (
          <Typography
            variant="micro"
            sx={{ typography: "eyebrow", color: tokens.brand }}
          >
            {t("signageScreenOverview.forcedChannelActive")}
          </Typography>
        )}
      </Stack>
      <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
        {/* Named as SCHEDULED whenever something is masking it, so
                        the two rows on the strip are legible from the rail. */}
        {overrideActive
          ? t("signageScreenOverview.playlistScheduled")
          : t("signageScreenOverview.playlist")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
      >
        {head.playlist.name}
      </Typography>
      <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
        {t("signageScreenOverview.itemsAndDuration", {
          count: head.playlist.items.length,
          duration: formatDuration(head.loopSeconds),
        })}
      </Typography>

      {(brk ?? head.item) && (
        <>
          <Typography variant="micro" sx={{ color: tokens.inkMuted, mt: 1.25 }}>
            {/*
                        Just "Now playing", whatever is on. WHICH campaign runs
                        WHEN is a question about windows and it is answered by
                        the lanes under this card, a row per campaign - not by
                        a label that is true for one minute and gone.
                      */}
            {t("signageScreenOverview.nowPlaying")}
          </Typography>
          <Typography
            variant="bodySm"
            noWrap
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {signageItemLabel(t, brk ? brk.item : head.item!, {
              assetName,
              // The loop the clip belongs to - a break item
              // belongs to its campaign, everything else to
              // whatever is currently on.
              channelName: brk
                ? (campaigns.find((c) => brk.brk.campaignIds.includes(c.id))
                    ?.name ?? t("signageScreenOverview.promotion"))
                : head.playlist.name,
            })}
          </Typography>
          {/* The panel's own elapsed when it has reported one, the
              schedule's theoretical position otherwise - see
              `liveElapsedSeconds`. The duration stays schedule-sourced
              either way: it is what the operator configured, not something
              a device reports. */}
          <Typography variant="micro" sx={{ color: tokens.brand, mt: 0.5 }}>
            {t("signageScreenOverview.clockOf", {
              elapsed: formatClock(
                liveElapsed ?? (brk ? brk.elapsedSeconds : head.elapsedSeconds)
              ),
              total: formatClock(brk ? brk.itemSeconds : head.itemSeconds),
            })}
          </Typography>
          {/*
                      The CURRENT ITEM's progress, not the loop's. A loop bar
                      would crawl for an hour and tell an operator nothing they
                      could act on; this one says whether the thing on screen
                      is about to change, which is what somebody watching a
                      wall is actually asking.

                      Can read past 100% when the live elapsed overruns the
                      scheduled duration - `progressPct` clamps the BAR, but
                      the number above is left unclamped on purpose: a bar
                      pinned at full and a clock stuck past its own duration
                      is the "stuck" signal, and clamping the number too
                      would hide it.
                    */}
          <LinearProgress
            variant="determinate"
            data-testid="signage-item-progress"
            value={progressPct(
              liveElapsed ?? (brk ? brk.elapsedSeconds : head.elapsedSeconds),
              brk ? brk.itemSeconds : head.itemSeconds
            )}
            sx={{ mt: 0.5, height: 3, borderRadius: 2 }}
          />
        </>
      )}
      {nextBreakSeconds != null && (
        <Typography
          variant="micro"
          data-testid="signage-next-break"
          sx={{ color: tokens.inkMuted, mt: 1 }}
        >
          {t("signageScreenOverview.nextBreakIn", {
            duration: formatDuration(nextBreakSeconds),
          })}
        </Typography>
      )}
    </Stack>
  ) : (
    <Stack gap={1}>
      {/*
                  Especially here. "Nothing scheduled" and "the panel is dark"
                  read identically in this rail, and only one of them wants
                  somebody to do something.
                */}
      <SignageLiveBadge lastSeenAt={lastSeenAt} report={report} now={now} />
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {t("signageScreenOverview.nothingScheduled")}
      </Typography>
      {nextBreakSeconds != null && (
        <Typography
          variant="micro"
          data-testid="signage-next-break"
          sx={{ color: tokens.inkMuted }}
        >
          {t("signageScreenOverview.nextBreakIn", {
            duration: formatDuration(nextBreakSeconds),
          })}
        </Typography>
      )}
    </Stack>
  );
}
